import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../common/auth/src/services/auth.service';
import { ToastService } from '../services';
import { AuthFlowService } from '../services/auth-flow/auth-flow.service';

/**
 * Interceptor that handles 401 Unauthorized responses
 * Automatically logs out the user when receiving 401 from backend
 * This ensures users are redirected to login when their session expires
 */
@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {
  /** Flag to prevent multiple simultaneous logouts */
  private _isLoggingOut = false;

  constructor(
    private readonly _authService: AuthService,
    private readonly _toastService: ToastService,
    private readonly _authFlowService: AuthFlowService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Check if this is a polling/retry request that should handle 401 gracefully
        const isPollingRequest = request.headers.has('X-Polling-Request');
        const suppressLogout = request.headers.has('X-Suppress-Logout');

        // Check if user creation polling is in progress via AuthFlowService
        const isUserCreationPolling = this._authFlowService.isPollingUserCreation();

        // ✅ HIGH FIX: Handle 401 Unauthorized - session expired or invalid token
        // During user creation polling, we allow 401 errors to propagate to component
        // so it can show appropriate message (token expired during registration)
        // BUT we don't auto-logout immediately - component handles the error flow
        if (error.status === 401 && !this._isLoggingOut) {
          // If polling is active, let component handle 401 (don't auto-logout)
          // Component will detect token expiration and show appropriate message
          if (isUserCreationPolling || isPollingRequest) {
            console.log('[UnauthorizedInterceptor] 401 during polling - letting component handle');
            // Component will handle this and show "session expired" message
            // Don't trigger auto-logout, but propagate error
            return throwError(() => error);
          }

          // For normal requests (not during polling), auto-logout on 401
          if (!suppressLogout) {
            this._handleUnauthorized();
          }
        }

        // Re-throw the error for other interceptors/components to handle
        return throwError(() => error);
      }),
    );
  }

  /**
   * Handle 401 Unauthorized response
   * Logs out user and redirects to login page
   */
  private _handleUnauthorized(): void {
    // Prevent multiple simultaneous logout calls
    if (this._isLoggingOut) {
      return;
    }

    this._isLoggingOut = true;

    // Show user-friendly message before logout
    // Using setTimeout to ensure toast is shown before page reload
    this._toastService.showInfo({
      title: 'Sessione scaduta',
      description: 'La tua sessione è scaduta. Effettua nuovamente il login.',
      duration: 2000,
    });

    // Small delay to show the toast before logout (which triggers page reload)
    setTimeout(() => {
      // Logout clears Cognito session and triggers window.location.reload()
      this._authService.logout();
    }, 300);
  }
}
